/**
 * Domain: Pitch mapping & scale quantization.
 *
 * Inspired by the static MIDI export script approach:
 * - map a value into a MIDI pitch range
 * - quantize that pitch to a musical scale
 *
 * No I/O, no dependencies.
 */

export type ScaleName =
  | 'chromatic'
  | 'major'
  | 'minor'
  | 'dorian'
  | 'phrygian'
  | 'lydian'
  | 'mixolydian'
  | 'locrian'
  | 'pentatonic';

export const SCALE_INTERVALS: Record<ScaleName, number[]> = {
  chromatic: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11],
  major: [0, 2, 4, 5, 7, 9, 11],
  minor: [0, 2, 3, 5, 7, 8, 10], // natural minor
  dorian: [0, 2, 3, 5, 7, 9, 10],
  phrygian: [0, 1, 3, 5, 7, 8, 10],
  lydian: [0, 2, 4, 6, 7, 9, 11],
  mixolydian: [0, 2, 4, 5, 7, 9, 10],
  locrian: [0, 1, 3, 5, 6, 8, 10],
  pentatonic: [0, 2, 4, 7, 9], // major pentatonic
};

/**
 * Clamp to inclusive [min, max].
 */
function clamp(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, n));
}

/**
 * Quantize a MIDI note number to the nearest note in the given scale.
 *
 * @param midiNote - integer-ish MIDI note number (0..127)
 * @param scale - scale name
 * @param root - pitch class root (0=C, 1=C#, ... 11=B)
 * @returns quantized MIDI note number (0..127)
 */
export function quantizeMidiNoteToScale(
  midiNote: number,
  scale: ScaleName,
  root: number = 0
): number {
  const intervals = SCALE_INTERVALS[scale] || SCALE_INTERVALS.chromatic;
  const bounded = clamp(Math.round(midiNote), 0, 127);

  // Find nearest pitch class in the chosen scale, and adjust octave accordingly.
  const pitchClass = ((bounded % 12) + 12) % 12;
  const rootPc = ((Math.round(root) % 12) + 12) % 12;
  const relPc = (pitchClass - rootPc + 12) % 12;

  // Find the closest interval (in semitones) in this octave (ties -> lower)
  let bestInterval = intervals[0];
  let bestDist = Infinity;
  for (const i of intervals) {
    const dist = Math.abs(i - relPc);
    if (dist < bestDist || (dist === bestDist && i < bestInterval)) {
      bestDist = dist;
      bestInterval = i;
    }
  }

  const octaveBase = bounded - relPc;
  const quantized = octaveBase + bestInterval;
  return clamp(quantized, 0, 127);
}

export interface PitchSettings {
  minValue: number;
  maxValue: number;
  lowNote: number;
  highNote: number;
  scale: ScaleName;
  root?: number;
  invertMapping?: boolean;
}

/**
 * Map a continuous value into a MIDI note range, then quantize to a scale.
 *
 * This mirrors the export script's idea of:
 * - linear mapping into [lowNote, highNote]
 * - quantize into a chosen scale
 *
 * @param value - input value to map
 * @param settings - pitch mapping settings
 * @returns MIDI note number (0..127)
 */
export function mapValueToMidiPitch(value: number, settings: PitchSettings): number {
  if (!settings) throw new Error('settings is required');
  const {
    minValue,
    maxValue,
    lowNote,
    highNote,
    scale,
    root = 0,
    invertMapping = false,
  } = settings;

  if (!Number.isFinite(minValue) || !Number.isFinite(maxValue) || maxValue <= minValue) {
    throw new Error('minValue/maxValue must be finite and maxValue must be > minValue');
  }
  if (!Number.isFinite(lowNote) || !Number.isFinite(highNote) || highNote < lowNote) {
    throw new Error('lowNote/highNote must be finite and highNote must be >= lowNote');
  }

  const v = Number(value);
  if (!Number.isFinite(v)) return quantizeMidiNoteToScale(lowNote, scale, root);

  const clampedValue = clamp(v, minValue, maxValue);
  const normalized = (clampedValue - minValue) / (maxValue - minValue);
  const mappedNormalized = invertMapping ? 1 - normalized : normalized;
  const unquantized = lowNote + mappedNormalized * (highNote - lowNote);
  return quantizeMidiNoteToScale(unquantized, scale, root);
}

